import { useCallback, useEffect, useState } from 'react';

import { apiClient } from '../api/client';

/**
 * ПОДПИСКА ТЕЛЕФОНА НА УВЕДОМЛЕНИЯ (`s608`, Ф4).
 *
 * Пока лайт открыт, о новом обращении говорит опрос ленты. Но открыт он
 * минуты в день: дежурный между клиентками. Всё остальное время говорить
 * может только уведомление, которое браузер принимает при закрытом окне.
 *
 * ⛔ РАЗРЕШЕНИЕ СПРАШИВАЕТСЯ ПО НАЖАТИЮ, А НЕ НА ВХОДЕ. Браузер телефона
 * показывает окно разрешения один раз: спроси его на открытии экрана — человек
 * отмахнётся не глядя, и вернуть вопрос будет уже нечем. Поэтому вопрос задаёт
 * колокольчик, который человек нажал сам.
 */
interface Sostoyanie {
  /** Умеет ли это устройство уведомления вообще (старый браузер, не-HTTPS). */
  dostupno: boolean;
  podpisan: boolean;
  /*
   * ⛔ ЗАПРЕТ — ОТДЕЛЬНАЯ ВЕЛИЧИНА ОТ «НЕ ПОДПИСАН» (`s615`). «Ещё не
   * включал» лечится нажатием, «запретил в настройках браузера» — не
   * лечится здесь вовсе, и предлагать кнопку в этом случае значит врать.
   */
  zapreshcheno: boolean;
  zanyat: boolean;
  podpis: string;
  pereklyuchit: () => Promise<void>;
}

const PUT_SW = '/chat-sw.js';

function umeet(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Ключ приезжает от сервера строкой, а браузеру нужен массивом байтов.
 *
 * ⛔ Буфер заводится ЯВНО (`new ArrayBuffer`), а не берётся у `new Uint8Array(n)`:
 * у второго тип буфера общий с разделяемой памятью, и подписка его не принимает.
 */
function klyuchVBayty(base64: string): ArrayBuffer {
  const rovno = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const obychnyy = rovno.replace(/-/g, '+').replace(/_/g, '/');
  const stroka = atob(obychnyy);
  const bufer = new ArrayBuffer(stroka.length);
  const bayty = new Uint8Array(bufer);
  for (let i = 0; i < stroka.length; i += 1) bayty[i] = stroka.charCodeAt(i);
  return bufer;
}

export function usePodpiskaNaPush(): Sostoyanie {
  const [podpisan, setPodpisan] = useState(false);
  const [zanyat, setZanyat] = useState(false);
  /*
   * ⛔ Запрет читается СРАЗУ при заводе состояния, а не отдельным шагом после
   * первой отрисовки: иначе колокольчик мигал бы «можно» и тут же «нельзя».
   */
  const [zapreshcheno, setZapreshcheno] = useState(
    () => umeet() && Notification.permission === 'denied',
  );

  const dostupno = umeet();

  // Что уже есть на этом устройстве — спрашиваем у браузера, а не помним сами:
  // человек мог отозвать разрешение в настройках, и наша память соврала бы.
  useEffect(() => {
    if (!dostupno) return;

    void navigator.serviceWorker
      .getRegistration(PUT_SW)
      .then((reg) => reg?.pushManager.getSubscription())
      .then((podpiska) => setPodpisan(podpiska != null))
      .catch(() => undefined);
  }, [dostupno]);

  const pereklyuchit = useCallback(async () => {
    if (!dostupno || zanyat) return;

    setZanyat(true);

    try {
      const rabotnik = await navigator.serviceWorker.register(PUT_SW);
      await navigator.serviceWorker.ready;

      const bylo = await rabotnik.pushManager.getSubscription();

      if (bylo != null) {
        // Отписка: сначала у нас, потом у браузера — иначе адрес пропал бы, и
        // снять подписку на сервере стало бы нечем.
        await apiClient.post('/push/otpiska', { endpoint: bylo.endpoint }).catch(() => undefined);
        await bylo.unsubscribe();
        setPodpisan(false);

        return;
      }

      const razreshenie = await Notification.requestPermission();

      if (razreshenie !== 'granted') {
        setZapreshcheno(razreshenie === 'denied');

        return;
      }

      const { data } = await apiClient.get<{ vklyucheny: boolean; klyuch: string }>('/push/klyuch');

      if (!data.vklyucheny || data.klyuch === '') {
        return;
      }

      const podpiska = await rabotnik.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: klyuchVBayty(data.klyuch),
      });

      const syroe = podpiska.toJSON() as { keys?: { p256dh?: string; auth?: string } };

      await apiClient.post('/push/podpiska', {
        endpoint: podpiska.endpoint,
        p256dh: syroe.keys?.p256dh ?? '',
        auth: syroe.keys?.auth ?? '',
      });

      setPodpisan(true);
    } catch {
      /*
       * ⛔ Молча и без падения: уведомления — украшение поверх переписки.
       * Сломайся подписка — экран обязан продолжать работать, а не встать.
       */
      setPodpisan(false);
    } finally {
      setZanyat(false);
    }
  }, [dostupno, zanyat]);

  const podpis = !dostupno
    ? 'Этот браузер не умеет уведомления'
    : zapreshcheno
      ? 'Уведомления запрещены в настройках браузера'
      : podpisan
        ? 'Уведомления включены — выключить'
        : 'Включить уведомления на этом телефоне';

  return { dostupno, podpisan, zapreshcheno, zanyat, podpis, pereklyuchit };
}
